import { Link } from 'gatsby'
import * as React from 'react'
import { ButtonSize, OutlinedButton, PrimaryButton } from 'streamr-ui'
import styled from 'styled-components'
import { BaseHero, Tiles } from '../../BaseHero'
import GovernanceLayer from '../assets/GovernanceLayer.png'
import GovernanceLayer2x from '../assets/GovernanceLayer@2x.png'
import IncentiveMechanism from '../assets/IncentiveMechanism.png'
import IncentiveMechanism2x from '../assets/IncentiveMechanism@2x.png'
import StackOfLovelyStreamrTokens from '../assets/StackOfLovelyStreamrTokens.png'
import StackOfLovelyStreamrTokens2x from '../assets/StackOfLovelyStreamrTokens@2x.png'
import { MqDesktop, MqTablet } from 'streamr-ui/css/consts'

export function Hero() {
    return (
        <HeroRoot
            tileOpacityRatio={2}
            icon={
                <img
                    src={StackOfLovelyStreamrTokens}
                    srcSet={`${StackOfLovelyStreamrTokens2x} 2x`}
                    alt="Tokens"
                />
            }
            content={
                <>
                    <h1>The DATA token</h1>
                    <h2>
                        DATA is the utility token of the Streamr Network,
                        powering&nbsp;decentralized data streaming.
                    </h2>
                    <ButtonList>
                        <PrimaryButton size={ButtonSize.Moose} tag="a" href="#get-data">
                            Get DATA
                        </PrimaryButton>
                        <OutlinedButton size={ButtonSize.Moose} tag={Link} to="/stake-and-earn">
                            Stake and Earn
                        </OutlinedButton>
                    </ButtonList>
                </>
            }
            tail={
                <Features>
                    <Feature>
                        <FeatureInner>
                            <h3>Incentive Mechanism</h3>
                            <p>
                                Node Operators must stake DATA tokens in order to earn from their
                                bandwidth contributions to the Streamr&nbsp;Network.
                            </p>
                            <p>
                                <a href="https://docs.streamr.network/streamr-network/incentives/network-incentives/">
                                    Learn more
                                    <ExternalLinkIcon />
                                </a>
                            </p>
                        </FeatureInner>
                        <FeatureImageWrap>
                            <img
                                src={IncentiveMechanism}
                                srcSet={`${IncentiveMechanism2x} 2x`}
                                alt="Incentive Mechanism"
                            />
                        </FeatureImageWrap>
                    </Feature>
                    <Feature>
                        <FeatureInner>
                            <h3>Governance Layer</h3>
                            <p>
                                Holding DATA tokens gives you voting rights, enabling you to
                                influence key network and economic decisions. This ensures the
                                network remains robust and decentralized, driven by
                                community&nbsp;participation.
                            </p>
                            <p>
                                <a href="https://docs.streamr.network/streamr-project/governance/">
                                    Learn more
                                    <ExternalLinkIcon />
                                </a>
                            </p>
                        </FeatureInner>
                        <FeatureImageWrap>
                            <img
                                src={GovernanceLayer}
                                srcSet={`${GovernanceLayer2x} 2x`}
                                alt="Governance Layer"
                            />
                        </FeatureImageWrap>
                    </Feature>
                </Features>
            }
        />
    )
}

const TwoColumnFeaturesMq = `(min-width: 992px)`

const FeatureImageWrap = styled.div`
    display: flex;
    justify-content: right;
    padding-right: 18px;
    margin-top: 48px;

    img {
        display: block;
        max-width: 200px;
        min-width: 100px;
        width: 50%;
    }

    @media ${TwoColumnFeaturesMq} {
        margin-top: 0;
    }
`

const FeatureInner = styled.div`
    flex-grow: 1;
`

function ExternalLinkIcon() {
    return (
        <svg
            width="26"
            height="26"
            viewBox="0 0 26 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.1562 8.70898C10.1562 8.37244 10.4291 8.09961 10.7656 8.09961H17.2656C17.6022 8.09961 17.875 8.37244 17.875 8.70898V15.209C17.875 15.5455 17.6022 15.8184 17.2656 15.8184C16.9291 15.8184 16.6563 15.5455 16.6563 15.209V9.31836H10.7656C10.4291 9.31836 10.1562 9.04553 10.1562 8.70898Z"
                fill="currentColor"
            />
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M17.6965 8.27809C17.9345 8.51607 17.9345 8.9019 17.6965 9.13988L8.75902 18.0774C8.52104 18.3154 8.13521 18.3154 7.89723 18.0774C7.65926 17.8394 7.65926 17.4536 7.89723 17.2156L16.8347 8.27809C17.0727 8.04012 17.4585 8.04012 17.6965 8.27809Z"
                fill="currentColor"
            />
        </svg>
    )
}

const Feature = styled.div`
    background: #ffffff;
    border-radius: 16px;
    box-sizing: border-box;
    padding: 60px 40px;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.02);

    h3 {
        font-size: 20px;
        line-height: 1.5;
        font-weight: 600;
        margin: 0 0 16px;
    }

    p {
        font-size: 16px;
        line-height: 28px;
        margin: 0;
    }

    p + p {
        margin-top: 16px;
    }

    a {
        font-size: 16px;
        line-height: 28px;
        align-items: center;
        display: flex;
        gap: 6px;
        font-weight: 500;
        color: #0324ff !important;
        text-decoration: none !important;
    }

    @media ${TwoColumnFeaturesMq} {
        height: 628px;
    }

    @media ${MqDesktop} {
        h3 {
            font-size: 24px;
        }

        p {
            font-size: 18px;
            line-height: 30px;
        }
    }
`

const Features = styled.div`
    display: grid;
    gap: 32px;
    grid-template-columns: 1fr;
    margin: -24px auto 0;
    width: 628px;
    max-width: 100%;
    padding: 0 32px;
    position: relative;
    z-index: 1;
    box-sizing: border-box;

    @media ${TwoColumnFeaturesMq} {
        padding: 0 64px;
        width: 1288px;
        grid-template-columns: 1fr 1fr;
    }

    @media ${MqTablet} {
        margin-top: -80px;
    }

    @media ${MqDesktop} {
        margin-top: -224px;
    }
`

const ButtonList = styled.div`
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    margin: 56px auto 0;
    max-width: 100%;
    width: 480px;
    padding: 0 32px;
    box-sizing: border-box;

    @media ${MqTablet} {
        grid-template-columns: auto auto;
        margin: 72px auto 0;
        max-width: none;
        width: max-content;
    }
`

const HeroRoot = styled(BaseHero)`
    --textColor: #0c009a;
    --bg: rgba(255, 255, 255, 0.8);

    background: #faf3ef;

    ${Tiles} {
        margin-top: 300px;
    }
`
